import { create } from 'zustand';

export type PlatformId = 'fb' | 'ig' | 'yt' | 'ln' | 'x' | 'tt';
export type ScheduleMode = 'now' | 'once' | 'recurring';
export type PostStatus = 'draft' | 'queued' | 'published' | 'error';

interface AuthData {
  accessToken: string;
  refreshToken?: string;
  tokenType: string;
  expiresIn: number;
  obtainedAt?: number;
}

interface Platform {
  id: PlatformId;
  enabled: boolean;
  name: string;
  color: string;
  icon: string;
  maxFileSize: number; // in MB
  characterLimits: {
    caption: number;
    title?: number;
    description?: number;
  };
}

interface PostingState {
  files: File[];
  platforms: Platform[];
  captions: Record<PlatformId, string>;
  titles: Record<PlatformId, string>;
  descriptions: Record<PlatformId, string>;
  thumbnails: Record<PlatformId, File | null>;
  schedule: {
    mode: ScheduleMode;
    datetime?: Date;
    cron?: string;
    timezone: string;
  };
  status: PostStatus;
  currentStep: number;
  errors: Record<PlatformId, string[]>;
  
  // Actions
  setFiles: (files: File[]) => void;
  addFiles: (files: File[]) => void;
  removeFile: (index: number) => void;
  togglePlatform: (id: PlatformId) => void;
  setCaption: (platformId: PlatformId, caption: string) => void;
  setTitle: (platformId: PlatformId, title: string) => void;
  setDescription: (platformId: PlatformId, description: string) => void;
  setThumbnail: (platformId: PlatformId, file: File | null) => void;
  setScheduleMode: (mode: ScheduleMode) => void;
  setScheduleDateTime: (datetime: Date) => void;
  setScheduleCron: (cron: string) => void;
  setScheduleTimezone: (timezone: string) => void;
  setStatus: (status: PostStatus) => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  addError: (platformId: PlatformId, error: string) => void;
  clearErrors: (platformId?: PlatformId) => void;
  reset: () => void;
  currentAuth: (platformId: PlatformId) => AuthData | null;
}

// Platform configuration
const platformsConfig: Platform[] = [
  {
    id: 'fb',
    enabled: false,
    name: 'Facebook',
    color: '#1877F2',
    icon: 'facebook',
    maxFileSize: 4000, // 4GB
    characterLimits: {
      caption: 63206, // Facebook's limit
    }
  },
  {
    id: 'ig',
    enabled: false,
    name: 'Instagram',
    color: '#E4405F',
    icon: 'instagram',
    maxFileSize: 100, // 100MB for videos
    characterLimits: {
      caption: 2200,
    }
  },
  {
    id: 'yt',
    enabled: false,
    name: 'YouTube',
    color: '#FF0000',
    icon: 'youtube',
    maxFileSize: 256000, // 256GB
    characterLimits: {
      caption: 5000, // Description
      title: 100,
    }
  },
  {
    id: 'ln',
    enabled: false,
    name: 'LinkedIn',
    color: '#0A66C2',
    icon: 'linkedin',
    maxFileSize: 200, // 200MB
    characterLimits: {
      caption: 3000,
    }
  },
  {
    id: 'x',
    enabled: false,
    name: 'X (Twitter)',
    color: '#1DA1F2',
    icon: 'twitter',
    maxFileSize: 512, // 512MB for videos
    characterLimits: {
      caption: 280,
    }
  },
  {
    id: 'tt',
    enabled: false,
    name: 'TikTok',
    color: '#000000',
    icon: 'tiktok',
    maxFileSize: 4000, // 4GB
    characterLimits: {
      caption: 2200,
    }
  }
];

// Create the store with initial state
export const usePostingStore = create<PostingState>((set, get) => ({
  files: [],
  platforms: platformsConfig,
  captions: {} as Record<PlatformId, string>,
  titles: {} as Record<PlatformId, string>,
  descriptions: {} as Record<PlatformId, string>,
  thumbnails: {} as Record<PlatformId, File | null>,
  schedule: {
    mode: 'now',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  },
  status: 'draft',
  currentStep: 1,
  errors: {} as Record<PlatformId, string[]>,

  setFiles: (files) => set({ files }),
  addFiles: (newFiles) => set((state) => ({ files: [...state.files, ...newFiles] })),
  removeFile: (index) => set((state) => ({ files: state.files.filter((_, i) => i !== index) })),
  togglePlatform: (id) => set((state) => ({
    platforms: state.platforms.map(p => 
      p.id === id ? { ...p, enabled: !p.enabled } : p
    )
  })),
  setCaption: (platformId, caption) => set((state) => ({
    captions: { ...state.captions, [platformId]: caption }
  })),
  setTitle: (platformId, title) => set((state) => ({
    titles: { ...state.titles, [platformId]: title }
  })),
  setDescription: (platformId, description) => set((state) => ({
    descriptions: { ...state.descriptions, [platformId]: description }
  })),
  setThumbnail: (platformId, file) => set((state) => ({
    thumbnails: { ...state.thumbnails, [platformId]: file }
  })),
  setScheduleMode: (mode) => set((state) => ({
    schedule: { ...state.schedule, mode }
  })),
  setScheduleDateTime: (datetime) => set((state) => ({
    schedule: { ...state.schedule, datetime }
  })),
  setScheduleCron: (cron) => set((state) => ({
    schedule: { ...state.schedule, cron }
  })),
  setScheduleTimezone: (timezone) => set((state) => ({
    schedule: { ...state.schedule, timezone }
  })),
  setStatus: (status) => set({ status }),
  nextStep: () => set((state) => ({ currentStep: state.currentStep + 1 })),
  prevStep: () => set((state) => ({ currentStep: state.currentStep - 1 })),
  goToStep: (step) => set({ currentStep: step }),
  addError: (platformId, error) => set((state) => ({
    errors: {
      ...state.errors,
      [platformId]: [...(state.errors[platformId] || []), error]
    }
  })),
  clearErrors: (platformId) => set((state) => ({
    errors: platformId 
      ? { ...state.errors, [platformId]: [] }
      : {} as Record<PlatformId, string[]>
  })),
  reset: () => set({
    files: [],
    captions: {} as Record<PlatformId, string>,
    titles: {} as Record<PlatformId, string>,
    descriptions: {} as Record<PlatformId, string>,
    thumbnails: {} as Record<PlatformId, File | null>,
    schedule: {
      mode: 'now',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    },
    status: 'draft',
    currentStep: 1,
    errors: {} as Record<PlatformId, string[]>,
    platforms: platformsConfig.map(p => ({ ...p, enabled: false }))
  }),
  currentAuth: () => null // This should be implemented based on your auth management
}));